<script lang="ts">
  /**
   * @deprecated use RadioGroup instead with `variant="button"`.
   * RadioGroup uses pretty much the same API as the ButtonGroup,
   * but every button is a radio element which makes more sense semantically.
   */
  export default {
    name: 'll-button-group',
  };
</script>

<script lang="ts" setup>
  import { uniqueId } from 'es-toolkit/compat';
  import { computed, ref } from 'vue';

  export interface ButtonGroupOption {
    id: string;
    active?: boolean;
    text: string;
  }

  export interface ButtonGroupProps {
    /**
     * A name for the group, useful when multiple ButtonGroups exist on the page
     */
    groupName?: string;

    /**
     * The list of buttons to render
     */
    options: ButtonGroupOption[];

    /**
     * Whether the group should expand to the parent's width
     */
    fullWidth?: boolean;

    /**
     * Whether the entire group should be disabled
     */
    disabled?: boolean;

    /**
     * Whether the active button can be deactivated
     */
    allowDeactivate?: boolean;
  }

  const props = withDefaults(defineProps<ButtonGroupProps>(), {
    groupName: uniqueId(),
    fullWidth: false,
    disabled: false,
    allowDeactivate: false,
  });

  const emit = defineEmits<{
    (e: 'input', activeButton: ButtonGroupOption | null): void;
  }>();

  const isSingleButton = computed(() => props.options.length === 1);

  const canDeactivate = computed(() => props.allowDeactivate || isSingleButton.value);

  const activeButton = ref(props.options.find((o) => o.active) || null);

  function onClick(clickedOption: ButtonGroupOption) {
    if (canDeactivate.value && activeButton.value?.id === clickedOption.id) {
      activeButton.value = null;
    } else {
      activeButton.value = clickedOption;
    }

    emit('input', activeButton.value);
  }
</script>

<template>
  <div
    :class="[
      'flex',
      $style.buttonGroup,
      { 'w-full': props.fullWidth },
      { [$style.single]: isSingleButton },
      { [$style['can-deactivate']]: canDeactivate },
    ]"
    :data-test="`button-group-${$props.groupName}`"
  >
    <button
      v-for="option in props.options"
      :key="`${props.groupName}-${option.id}`"
      :class="[$style.button, { [$style.active]: activeButton?.id === option.id }]"
      :data-test="`${props.groupName}-button-${activeButton?.id === option.id ? 'active' : 'inactive'}`"
      :tabindex="0"
      :disabled="props.disabled"
      @click.prevent="onClick(option)"
    >
      {{ option.text }}
    </button>
  </div>
</template>

<style module>
  /* full width buttons */
  .buttonGroup:global(.w-full) > button {
    flex: 1 1 50%;
    max-width: 100%;
  }

  .button {
    border: 1px solid var(--color-ice-500);
    border-radius: 0;
    font-weight: var(--stash-font-weight-semibold);
    line-height: var(--line-height-body);
    min-width: 100px;
    padding: 7px 8px;
  }

  .single .button {
    border: 1px solid var(--color-ice-700);
    color: var(--color-ice-700);
  }

  .button:focus,
  .button:hover {
    border: 1px solid var(--color-blue-500) !important;
    color: var(--color-blue-500);
    z-index: 1;
  }

  .button.active {
    background-color: var(--color-blue-100);
    border: 1px solid var(--color-blue-500) !important;
    color: var(--color-blue-500);
    cursor: default;
    z-index: 1;
  }

  .button.active + button {
    border-left: 1px solid transparent;
  }

  .single .button.active,
  .can-deactivate .button.active {
    cursor: pointer;
  }

  .button[disabled] {
    color: var(--color-ice-500);
    cursor: default;
    pointer-events: none;
  }

  .button:focus {
    box-shadow: none;
    outline: 0;
  }

  .button:not(:first-child) {
    margin-left: -1px;
  }

  .button:not(:last-child) {
    border-right: 1px solid transparent;
  }

  .button:first-child {
    border-bottom-left-radius: var(--radius-sm);
    border-top-left-radius: var(--radius-sm);
  }

  .button:last-child {
    border-bottom-right-radius: var(--radius-sm);
    border-top-right-radius: var(--radius-sm);
  }
</style>
